export interface BoardLayout {
  cellSize: number;
  boardWidth: number;
  boardHeight: number;
  originX: number;
  originY: number;
}

/**
 * Fits a `cols x rows` grid of square cells inside `viewWidth x viewHeight`,
 * leaving `topReserved` / `bottomReserved` pixels of vertical room for HUD
 * elements (score, pause button, etc.), and centers the result.
 *
 * Used on every resize so the board keeps square cells and a fixed aspect
 * ratio, in both portrait and landscape, without ever changing game rules.
 */
export function computeBoardLayout(
  viewWidth: number,
  viewHeight: number,
  cols: number,
  rows: number,
  topReserved: number,
  bottomReserved: number,
  sidePadding: number,
): BoardLayout {
  const availableWidth = Math.max(1, viewWidth - sidePadding * 2);
  const availableHeight = Math.max(1, viewHeight - topReserved - bottomReserved);

  const cellFromWidth = availableWidth / cols;
  const cellFromHeight = availableHeight / rows;
  const cellSize = Math.floor(Math.min(cellFromWidth, cellFromHeight));

  const boardWidth = cellSize * cols;
  const boardHeight = cellSize * rows;

  const originX = (viewWidth - boardWidth) / 2;
  const originY = topReserved + (availableHeight - boardHeight) / 2;

  return { cellSize, boardWidth, boardHeight, originX, originY };
}

export interface EdgeInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * PR31 (corrected: portrait-only mobile gameplay). Mobile gameplay is
 * portrait-only (see `ViewportClassification.ts`), so a phone playing the
 * game uses the exact same top-reserved-HUD-strip board layout
 * `computeBoardLayout()` already produces for a desktop window - there is
 * no separate phone layout algorithm to maintain (an earlier landscape-
 * only revision of this PR added one, a side HUD column; it was reverted
 * along with the orientation direction itself).
 *
 * The one thing phone gameplay needs on top of `computeBoardLayout()`
 * that desktop never does is safe-area awareness: `insets` (notch/Dynamic
 * Island/rounded corners, see `readSafeAreaInsets()`) are subtracted from
 * the available space on every edge BEFORE fitting the board into it, and
 * added back as an offset to the final board position - so the board
 * never sits underneath a physical obstruction, without hardcoding any
 * specific device's exact notch geometry. Safe to call unconditionally
 * for every viewport, not just phones: `readSafeAreaInsets()` already
 * returns all-zero on a desktop browser (no `env()` support needed, no
 * notch to avoid), so this is then arithmetically identical to calling
 * `computeBoardLayout()` directly - which is exactly why `GameScene` no
 * longer branches on `isPhoneLikeViewport()` for layout at all, only for
 * the orientation gate itself.
 *
 * Pure and framework-independent (no `window`/DOM access) - see
 * `readSafeAreaInsets()`/`readViewportSignals()` for the live browser
 * reads this is fed from, kept separate so this stays directly unit-
 * testable.
 */
export function computeInsetBoardLayout(
  viewWidth: number,
  viewHeight: number,
  cols: number,
  rows: number,
  insets: EdgeInsets,
  topReserved: number,
  bottomReserved: number,
  sidePadding: number,
): BoardLayout {
  const areaWidth = Math.max(1, viewWidth - insets.left - insets.right);
  const areaHeight = Math.max(1, viewHeight - insets.top - insets.bottom);

  const board = computeBoardLayout(areaWidth, areaHeight, cols, rows, topReserved, bottomReserved, sidePadding);

  return {
    ...board,
    originX: board.originX + insets.left,
    originY: board.originY + insets.top,
  };
}
